import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ConfirmationMessageModal,
  FullScreenLoader,
  MenuItem,
  ScriptsTable,
  ScrollToTop,
  TableActionsMenu,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import ScriptsModal from '@Containers/Scripts/ScriptsModal';
import { MetaData } from '@Models/common.models';
import { ScriptsQuery } from '@Models/scripts.models';
import {
  useArchiveScriptsMutation,
  useDeleteScriptsMutation,
  useGetScriptsListsQuery,
} from '@Services/scripts.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
  DialerTypes,
} from '@Typings/global.types';
import {
  getFilters,
  GetRows,
  getRTKQuery,
  getModalBtnComponent,
  getConfirmationMessageNode,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const ScriptsArchived: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAlert } = useAlert();

  /** STATES */
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [scriptId, setScriptId] = useState<string>('');
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);

  const scriptsArchivedQuery: ScriptsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );

  /** RTK QUERY HOOK */
  const {
    data,
    isLoading,
    isFetching,
    error: getScriptsError,
  } = useGetScriptsListsQuery({
    data: scriptsArchivedQuery,
    isArchive: true,
  });
  const [archiveScripts, archiveMethods] = useArchiveScriptsMutation();
  const [deleteScripts, deleteMethods] = useDeleteScriptsMutation();

  /** TABLE LOADING STATE */
  const loading =
    isLoading ||
    isFetching ||
    archiveMethods.isLoading ||
    deleteMethods.isLoading;

  // show error for get query
  useEffect(() => {
    if (getScriptsError) {
      triggerAlert(getScriptsError, setAlertState);
    }
  }, [getScriptsError, triggerAlert]);

  /** UPDATES THE CHECKED STATE AFTER BULK ACTIONS */
  useEffect(() => {
    setScriptId('');
    setIsBulkAction(false);
    setItemsCount(0);

    if (archiveMethods.isSuccess) {
      setChecked([]);
    }

    if (deleteMethods.isSuccess) {
      setChecked([]);
    }
  }, [archiveMethods.isSuccess, deleteMethods.isSuccess]);

  /** TRIGGERS SUCCESS OR ERROR ALERT AFTER ACTIONS */
  useEffect(() => {
    // UNARCHIVE SUCCESS
    if (archiveMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.UNARCHIVE_SCRIPT,
        setAlertState
      );
    }

    // UNARCHIVE ERROR
    if (archiveMethods.isError) {
      triggerAlert(archiveMethods.error, setAlertState);
    }
  }, [
    triggerAlert,
    archiveMethods.isSuccess,
    archiveMethods.isError,
    archiveMethods.error,
  ]);

  useEffect(() => {
    // DELETE SUCCESS
    if (deleteMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.DELETE_SCRIPT,
        setAlertState
      );
    }

    // DELETE ERROR
    if (deleteMethods.error) {
      triggerAlert(deleteMethods.error, setAlertState);
    }
  }, [triggerAlert, deleteMethods.isSuccess, deleteMethods.error]);

  /** SCRIPTS ACTION HANDLERS --> */
  /** HANDLES SCRIPTS ARCHIVE */
  const handleUnarchive = (ids: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse(), true);

    return itemsCount
      ? archiveScripts({
          ...metaData,
          archive: false,
          itemsCount,
          userId: `${userId}`,
        })
      : archiveScripts({ ids, archive: false, userId: `${userId}` });
  };

  /** HANDLES SCRIPTS DELETE */
  const handleDelete = (ids?: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse(), true);

    return itemsCount
      ? deleteScripts({ ...metaData, itemsCount, userId: `${userId}` })
      : deleteScripts({ ids, userId: `${userId}` });
  };
  /** <-- SCRIPTS ACTION HANDLERS */

  /** Table Modal Button Component Function
   *
   * Returns the Modal Button component which opens modal when clicked.
   */
  const modalBtnComponent = (id: string, name: string): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.Archive);
      setScriptId(id);
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
    });
  };

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return isBulkAction ? handleDelete(checked) : handleDelete([scriptId]);
    }
    if (confirmationActionType === ConfirmationActionTypes.Unarchive) {
      return isBulkAction
        ? handleUnarchive(checked)
        : handleUnarchive([scriptId]);
    }
  };

  /** Table Actions Component Function
   *
   * Returns the table actions component with required menu items.
   */
  const actionsComponent = (id: string, disabled?: boolean): JSX.Element => {
    /** Handles click of action menu items */
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
    };

    /** Actions Menu Data */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setScriptId(id);
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
      },
      {
        label: t('common.messages.Unarchive'),
        icon: 'unarchive',
        value: id,
        onClick: () => {
          setScriptId(id);
          handleTableActionsClick(ConfirmationActionTypes.Unarchive);
        },
      },
    ];

    return <TableActionsMenu menuItems={actionsMenu} disabled={disabled} />;
  };

  /** ROWS & TOTAL_ROWS_COUNT */
  const rows = GetRows(data?.list, actionsComponent, modalBtnComponent);
  const totalRowsCount = data?.meta.count
    ? parseInt(data.meta.count, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  /** Handle Bulk Count
   * Callback to update ItemsCount
   */
  const handleBulk = (count: number) => setItemsCount(count);

  /** Handles Bulk action button click */
  const handleBulkActionBtnClick = (
    confirmationType: ConfirmationActionTypes
  ) => {
    setConfirmationActionType(confirmationType);
    setIsBulkAction(true);
    setIsConfirmationModalOpen(true);
  };

  return !isLoading ? (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <ScriptsModal
        scriptId={scriptId}
        setScriptId={setScriptId}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        unarchiveBtnOnClick={() => handleUnarchive([scriptId])}
        deleteBtnOnClick={() => handleDelete([scriptId])}
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(confirmationActionType)}
        onClick={handleConfirmationModalClick}
      />
      <ScriptsTable
        heading={query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) && <></>}
        selectedBtnNumber={itemsCount || checked.length}
        tabChild={!!query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID)}
        alertState={alertState}
        scriptType={DialerTypes.Archived}
        archiveUnarchiveBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Unarchive);
          },
          disabled: loading,
        }}
        deleteBtnProps={{
          onClick: () => {
            handleBulkActionBtnClick(ConfirmationActionTypes.Delete);
          },
          disabled: loading,
        }}
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
        handleBulk={handleBulk}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default ScriptsArchived;
